import React, { useCallback, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport, useWmsItemGroup } from "hooks";
import { ServiceRevenueQuery, ServiceRevenueRecord } from "types";
import { formatDateSafe } from "utils/helpers/accounting";
import SelectMulti from "components/ui-common/SelectMulti";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: ServiceRevenueQuery = {
  from_date: startOfYear,
  to_date: todayString,
  group_ids: [],
};

const ServiceRevenueReport: React.FC = () => {
  const { fetchServiceRevenue } = useReport();
  const { fetchList: fetchWmsGroups } = useWmsItemGroup();
  const { data: wmsGroups = [] } = fetchWmsGroups(true);
  const groupOptions = useMemo(
    () =>
      (wmsGroups as any[])?.map?.((g: any) => {
        const vatRate = Number(g.vat_ratio ?? 0);
        const pitRate = Number(g.pit_ratio ?? 0);
        const label = [
          g.name ?? g.code ?? "",
          `GTGT ${vatRate}% - TNCN ${pitRate}%`,
        ]
          .filter(Boolean)
          .join(" ");
        return {
          value: String(g.id),
          label,
        };
      }) ?? [],
    [wmsGroups],
  );
  const [filters, setFilters] = useState<ServiceRevenueQuery>(defaultFilters);
  const [submitted, setSubmitted] = useState<ServiceRevenueQuery | null>(null);

  const handleFilterChange = useCallback(
    (key: keyof ServiceRevenueQuery, value: any) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchServiceRevenue.mutate(filters);
    },
    [fetchServiceRevenue, filters],
  );

  const records = (fetchServiceRevenue.data?.data ??
    []) as ServiceRevenueRecord[];
  const totals = useMemo(
    () =>
      records.reduce(
        (acc, item) => {
          const taxable = Number(item.taxable_revenue ?? item.amount ?? 0);
          acc.taxable_revenue += Number.isFinite(taxable) ? taxable : 0;
          acc.vat_amount += Number(item.vat_amount ?? 0) || 0;
          acc.pit_amount += Number(item.pit_amount ?? 0) || 0;
          return acc;
        },
        { taxable_revenue: 0, vat_amount: 0, pit_amount: 0 },
      ),
    [records],
  );

  const columns = useMemo(
    () => [
      {
        headerName: "Mã chứng từ",
        field: "order_code",
        minWidth: 160,
        valueGetter: (params: any) =>
          (params.data?.order_code as string) ??
          (params.data?.code as string) ??
          "",
      },
      {
        headerName: "Ngày hạch toán",
        field: "order_date",
        minWidth: 140,
        valueFormatter: (params: any) => {
          const value =
            (params.data?.order_date as string) ??
            (params.data?.accounting_date as string) ??
            params.value;
          return formatDateSafe(value);
        },
      },
      {
        headerName: "Nội dung",
        field: "description",
        minWidth: 260,
        flex: 1,
      },
      {
        headerName: "Nhóm hàng/dịch vụ",
        field: "group_name",
        minWidth: 200,
        valueGetter: (params: any) =>
          params.data?.group_name ?? params.data?.tax_group_name ?? "",
      },
      {
        headerName: "Doanh thu tính thuế",
        field: "taxable_revenue",
        minWidth: 160,
        valueGetter: (params: any) =>
          params.data?.taxable_revenue ?? params.data?.amount ?? 0,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
      {
        headerName: "Thuế GTGT",
        field: "vat_amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
      {
        headerName: "Thuế TNCN",
        field: "pit_amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN") + " ₫"
            : "",
        cellClass: "text-right font-semibold text-gray-900",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [],
  );

  const isLoading = fetchServiceRevenue.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase">Báo cáo S1-HKD</p>
          <h1 className="text-2xl font-semibold text-gray-900">
            Sổ chi tiết doanh thu bán hàng hóa, dịch vụ
          </h1>
          <p className="text-sm text-gray-600">
            Lọc theo thời gian và nhóm hàng/dịch vụ. Từ ngày và đến ngày là bắt
            buộc.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading ? "Đang tải..." : "Xem báo cáo"}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Lọc theo nhóm hàng/dịch vụ (tùy chọn)
          </label>
          <SelectMulti
            isMulti
            isClearable
            options={groupOptions}
            value={groupOptions.filter((opt) =>
              (filters.group_ids ?? []).map(String).includes(opt.value),
            )}
            placeholder="Chọn nhóm hàng/dịch vụ - Để trống để xem tất cả"
            onChange={(vals: any) =>
              handleFilterChange(
                "group_ids",
                Array.isArray(vals) ? vals.map((v: any) => v.value) : [],
              )
            }
            className="react-select-container"
            classNamePrefix="react-select"
          />
          <p className="text-xs text-gray-500">
            💡 Hiển thị thuế suất GTGT và TNCN của từng nhóm. Có thể chọn nhiều
            nhóm cùng lúc.
          </p>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            Kết quả{" "}
            {submitted
              ? `( ${submitted.from_date} → ${submitted.to_date} )`
              : ""}
          </h2>
          <div className="flex flex-wrap items-center gap-6 text-right">
            <div>
              <p className="text-xs text-gray-500">Tổng doanh thu tính thuế</p>
              <p className="text-lg font-semibold text-gray-900">
                {totals.taxable_revenue.toLocaleString("vi-VN")} ₫
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tổng thuế GTGT</p>
              <p className="text-lg font-semibold text-gray-900">
                {totals.vat_amount.toLocaleString("vi-VN")} ₫
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tổng thuế TNCN</p>
              <p className="text-lg font-semibold text-gray-900">
                {totals.pit_amount.toLocaleString("vi-VN")} ₫
              </p>
            </div>
          </div>
        </div>
        <AgGridTable
          rows={records}
          columns={columns as any}
          defaultColDef={defaultColDef}
          pagination={false}
          autoHeight
        />
      </div>
    </div>
  );
};

export default ServiceRevenueReport;
